import PropTypes from 'prop-types';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MicrophoneIconButton from 'components/UI/Buttons/MicrophoneIconButton/MicrophoneIconButton';
import { IconButton, Tooltip } from '@mui/material';
import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';
import { talkManager } from 'services/Talk/TalkManager';
import { start3CXCall } from 'utils/threeCX';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import * as transactionsSelectors from 'store/transactionsSlice/transactionsSelectors';
import useAgentAudioTimeTracker from 'hooks/transaction/useAgentAudioTimeTracker';

function TwoWayAudio({
    enableDemoAudioFile,
    onToggleAudio,
    camVideoId,
    camConfig,
    className,
    tooltipPopperProps,
    showTooltip = true,
    transactionId,
}) {
    const isTalkSessionStarting = useSelector(transactionsSelectors.selectTalkSessionStarting);
    const activeTalkUrl = useSelector(transactionsSelectors.selectActiveTalkUrl);
    const activeDemoTalkUrl = useSelector(transactionsSelectors.selectActiveDemoTalkUrl);
    const isTalkSessionPaused = useSelector(transactionsSelectors.selectTalkSessionPaused);
    const { talkUrl, camId, name, extensionNumber3CX } = camConfig;
    const isTalkUrlActive = talkUrl === activeTalkUrl;
    const isDemoTalkUrlActive = talkUrl === activeDemoTalkUrl;
    const isCameraActive = camConfig?.status !== 'INACTIVE';
    const markAgentAudioTime = useAgentAudioTimeTracker({ transactionId });

    const talkActivationHandler = useCallback(
        async function () {
            if (activeTalkUrl && talkUrl === activeTalkUrl) {
                if (isTalkSessionPaused) {
                    talkManager.toggleOutgoingAudioActive();
                    return;
                }
                await talkManager.stopSession();
            } else {
                onToggleAudio && onToggleAudio({ muteAll: true });
                await talkManager.startSession({ talkUrl }, camId, name);
                markAgentAudioTime();
            }
        },
        [activeTalkUrl, talkUrl, onToggleAudio, isTalkSessionPaused, camId, name, markAgentAudioTime]
    );

    const talkActivationDemoFileHandler = useCallback(
        async function () {
            if (activeDemoTalkUrl && talkUrl === activeDemoTalkUrl) await talkManager.stopDemoFile();
            else {
                !cameraStreamManager.isAudioEnabled(camVideoId) && onToggleAudio();
                await talkManager.playDemoFile(talkUrl, camId);
            }
        },
        [talkUrl, activeDemoTalkUrl, onToggleAudio, camVideoId, camId]
    );

    // Release Manager note: When pulling the main branch into develop, please keep these Call 3CX changes on develop, as this Kiosk support should currently remain dev-only.
    const start3CXHandler = () => {
        start3CXCall(extensionNumber3CX);
    };

    return (
        <>
            {extensionNumber3CX && isCameraActive && (
                <div className={className}>
                    <Tooltip title={showTooltip ? 'Start 3CX extension' : ''} PopperProps={tooltipPopperProps}>
                        <IconButton
                            sx={{
                                color: 'white',
                                padding: 0,

                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                            }}
                            onClick={start3CXHandler}
                            size="small"
                        >
                            <MicIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </div>
            )}

            {talkUrl && isCameraActive && (
                <div className={className}>
                    {enableDemoAudioFile && (
                        <IconButton
                            onClick={talkActivationDemoFileHandler}
                            disabled={!!activeTalkUrl || (!!activeDemoTalkUrl && !isDemoTalkUrlActive)}
                            sx={{
                                color: 'white',
                                padding: 0,

                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                            }}
                        >
                            {!isDemoTalkUrlActive && (
                                <Tooltip title="Play pre-recorded audio file">
                                    <PlayArrowIcon fontSize="small" />
                                </Tooltip>
                            )}
                            {isDemoTalkUrlActive && (
                                <Tooltip title="Stop pre-recorded audio file">
                                    <StopIcon fontSize="small" color="error" />
                                </Tooltip>
                            )}
                        </IconButton>
                    )}

                    <MicrophoneIconButton
                        fontSize="small"
                        containerStyles={{
                            color: 'white',
                            padding: 0,

                            '&:hover': {
                                backgroundColor: 'transparent',
                            },
                        }}
                        iconProps={{ fontSize: 'medium' }}
                        active={isTalkUrlActive}
                        starting={isTalkUrlActive && isTalkSessionStarting}
                        paused={isTalkUrlActive && isTalkSessionPaused}
                        disabled={!isTalkUrlActive && isTalkSessionStarting}
                        onClick={talkActivationHandler}
                        cameraId={camId}
                        showTooltip={showTooltip}
                        tooltipPopperProps={tooltipPopperProps}
                    />
                </div>
            )}
        </>
    );
}

TwoWayAudio.propTypes = {
    enableDemoAudioFile: PropTypes.bool,
    onToggleAudio: PropTypes.func,
    camVideoId: PropTypes.string,
    camConfig: PropTypes.object.isRequired,
    className: PropTypes.string,
    tooltipPopperProps: PropTypes.object,
    showTooltip: PropTypes.bool,
    transactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default TwoWayAudio;
